import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { t } from "i18next";

import { errors } from "../../constants";
import { RequestBodyModel, TApiResponse, TApiSuccess } from "../../models";
import { prepareAxiosRequestConfig } from "../auth/prepare-axios-request-config";
import { Log } from "../log.helpers";
import { storeCurrentPageAndRedirectToLogin } from "../url.helpers";
import {
  shouldRedirectToLoginForApiException,
  shouldRedirectToLoginForAxiosUnauthorized,
} from "./api-utils.auth-session-redirect.helpers";
import { checkResponseStatus } from "./api-utils.check-response";
import { handleApiException } from "./api-utils.exception-handler";
import { showTokenExpiredAlert } from "./api-utils.token-expired-alert";
import { installAxiosSessionDefaults } from "./axios-session-defaults";

installAxiosSessionDefaults();

export const doApiCall = async <T>(
  axiosConfig: AxiosRequestConfig<RequestBodyModel>,
): Promise<TApiSuccess<T> | undefined> => {
  try {
    if (!navigator.onLine) {
      Log.warnAndToast(t(errors.common.internetConnectionUnstable));
    }

    const updatedConfig = await prepareAxiosRequestConfig(axiosConfig);
    const response = (await axios(updatedConfig)) as AxiosResponse<
      TApiResponse<T>
    >;
    return await checkResponseStatus(response);
  } catch (e) {
    Log.debug("Response axios exception ", e);
    let isExceptionHandled = false;

    if (!navigator.onLine) {
      Log.warnAndToast(t(errors.common.internetConnectionIssue));
    }

    try {
      const response = await handleApiException<T>({ e, axiosConfig });

      if (response) {
        Log.debug("Response after handling api error", response);
        return await checkResponseStatus(response);
      }
    } catch (apiError) {
      Log.debug("Second-level api error from handleApiException: ", apiError);

      if (shouldRedirectToLoginForApiException({ apiError, axiosConfig })) {
        isExceptionHandled = true;
        Log.debug(
          "Api session-ended error handled, redirecting to login page ",
          apiError,
          apiError instanceof Error ? apiError.message : apiError,
        );
        showTokenExpiredAlert();
        storeCurrentPageAndRedirectToLogin();
      } else {
        throw apiError;
      }
    }

    if (!isExceptionHandled) {
      if (shouldRedirectToLoginForAxiosUnauthorized(e, axiosConfig)) {
        showTokenExpiredAlert();
        storeCurrentPageAndRedirectToLogin();
        return undefined;
      }
      throw e;
    }

    return undefined;
  }
};

export const checkVersionCall = async <T>(
  axiosConfig: AxiosRequestConfig<RequestBodyModel>,
): Promise<AxiosResponse<T>> => {
  try {
    return await axios(axiosConfig);
  } catch (e) {
    throw await handleApiException({ e, axiosConfig });
  }
};
